import type { FormStep } from '@/api/types/Forms'
import { normalizeState } from './usStates'

/**
 * What the patient has entered on each kind of step.
 *
 * CHOICE is keyed by option id exactly as web-core's Quiz kept it, with sub-options sharing the
 * map, so the answers extracted from it come out identical to what the web sent.
 */
export type ChoiceState = Record<string, { value?: string }>

export interface DateState {
	month: string
	day: string
	year: string
}

export interface AddressState {
	street1: string
	street2: string
	city: string
	state: string
	zip: string
}

export type StepState = string | ChoiceState | DateState | AddressState

export type FormStates = Record<number, StepState | undefined>

export const EMPTY_DATE: DateState = { month: '', day: '', year: '' }

export const EMPTY_ADDRESS: AddressState = {
	street1: '',
	street2: '',
	city: '',
	state: '',
	zip: '',
}

export const MIN_BIRTH_YEAR = 1900

export const getQuestionState = (state: StepState | undefined): string =>
	typeof state === 'string' ? state : ''

export const getChoiceState = (state: StepState | undefined): ChoiceState =>
	state && typeof state === 'object' && !('month' in state) && !('street1' in state)
		? (state as ChoiceState)
		: {}

export const getDateState = (state: StepState | undefined): DateState =>
	state && typeof state === 'object' && 'month' in state
		? (state as DateState)
		: EMPTY_DATE

export const getAddressState = (state: StepState | undefined): AddressState =>
	state && typeof state === 'object' && 'street1' in state
		? (state as AddressState)
		: EMPTY_ADDRESS

const toInteger = (value: string) =>
	/^\d+$/.test(value) ? Number.parseInt(value, 10) : null

/** A real calendar date within the range a date of birth or a last cleaning can fall in. */
export const isDateComplete = (date: DateState, now = new Date()): boolean => {
	const month = toInteger(date.month)
	const day = toInteger(date.day)
	const year = toInteger(date.year)

	if (month === null || day === null || year === null) {
		return false
	}
	if (month < 1 || month > 12 || day < 1 || day > 31) {
		return false
	}
	if (year < MIN_BIRTH_YEAR || year > now.getFullYear()) {
		return false
	}

	const candidate = new Date(year, month - 1, day)
	return candidate.getMonth() === month - 1 && candidate.getDate() === day
}

export const isAddressComplete = (address: AddressState): boolean =>
	!!address.street1.trim() &&
	!!address.city.trim() &&
	!!address.zip.trim() &&
	normalizeState(address.state) !== null

/** Whether the step has an answer the patient could move on with. */
export const isStepComplete = (step: FormStep, state: StepState | undefined): boolean => {
	switch (step.type) {
		case 'INFO':
		case 'CONSENT':
			return true
		case 'QUESTION':
			return getQuestionState(state).trim().length > 0
		case 'CHOICE':
			return Object.keys(getChoiceState(state)).length > 0
		case 'DATE':
			return isDateComplete(getDateState(state))
		case 'ADDRESS':
			return isAddressComplete(getAddressState(state))
	}
}

/** Whether the primary button should be enabled. */
export const canProceed = (step: FormStep, state: StepState | undefined): boolean =>
	!!step.canSkip || isStepComplete(step, state)

/** What an optional step with nothing entered offers instead of moving on. */
export const SKIP_LABEL = 'Skip'

/**
 * The primary button's wording, as the web renderer chose it: a skippable step with nothing
 * entered offers "Skip", otherwise the step's own label, falling back to "Next".
 */
export const resolveButtonLabel = (
	step: FormStep,
	state: StepState | undefined
): string => {
	if (step.type === 'INFO') {
		return step.cta ?? 'Next'
	}
	if (step.canSkip && !isStepComplete(step, state)) {
		return SKIP_LABEL
	}
	return step.primaryButtonLabel ?? 'Next'
}

/** Toggles a selectable option or sub-option, as web-core's `toggleSelectableItem` did. */
export const toggleChoice = (state: ChoiceState, optionId: number): ChoiceState => {
	const key = String(optionId)
	if (key in state) {
		const { [key]: _removed, ...rest } = state
		return rest
	}
	return { ...state, [key]: {} }
}

/** Records typed "other" text; clearing it deselects the option. */
export const setEditableChoice = (
	state: ChoiceState,
	optionId: number,
	value: string
): ChoiceState => {
	const key = String(optionId)
	if (!value) {
		const { [key]: _removed, ...rest } = state
		return rest
	}
	return { ...state, [key]: { value } }
}
